"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, MapPin, Phone } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  message: z.string().min(10, "Message must be at least 10 characters")
});
export default function ContactUs() {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: ""
    }
  });
  function onSubmit(values: z.infer<typeof formSchema>) {
    console.log(values);
  }
  const contactInfo = [{
    icon: Phone,
    label: "Phone",
    value: "(305) 342-5287",
    href: "tel:+13053425287"
  }, {
    icon: Mail,
    label: "Email",
    value: "alvaro@arangroup.net",
    href: "mailto:alvaro@arangroup.net"
  }, {
    icon: MapPin,
    label: "Address",
    value: "16660 85th Ln, Miami, FL 33193",
    href: ""
  }];
  return <section className="bg-[oklch(0.18_0.03_220)] py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-12">
          <h2 className="text-white font-display text-4xl font-bold tracking-tight mb-4">
            Contact Us
          </h2>
          <p className="text-[oklch(0.90_0_0)] text-lg">
            Get in touch with Aran Group to discuss how we can help your
            business grow
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="animate-fade-in-up">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 shadow-medium">
                <FormField control={form.control} name="name" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-white">Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your name" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[oklch(0.50_0.13_150)] focus:ring-[oklch(0.50_0.13_150)]/30" {...field} />
                      </FormControl>
                      <FormMessage className="text-[oklch(0.50_0.13_150)]" />
                    </FormItem>} />

                <FormField control={form.control} name="email" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-white">Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="your@email.com" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[oklch(0.50_0.13_150)] focus:ring-[oklch(0.50_0.13_150)]/30" {...field} />
                      </FormControl>
                      <FormMessage className="text-[oklch(0.50_0.13_150)]" />
                    </FormItem>} />

                <FormField control={form.control} name="phone" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-white">Phone</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="(305) 342-5287" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[oklch(0.50_0.13_150)] focus:ring-[oklch(0.50_0.13_150)]/30" {...field} />
                      </FormControl>
                      <FormMessage className="text-[oklch(0.50_0.13_150)]" />
                    </FormItem>} />

                <FormField control={form.control} name="message" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-white">Message</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Tell us about your project..." className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[oklch(0.50_0.13_150)] focus:ring-[oklch(0.50_0.13_150)]/30 min-h-32" {...field} />
                      </FormControl>
                      <FormMessage className="text-[oklch(0.50_0.13_150)]" />
                    </FormItem>} />

                <Button type="submit" className="w-full bg-[oklch(0.50_0.13_150)] hover:bg-[oklch(0.55_0.13_150)] text-white font-semibold py-3 rounded-md shadow-medium transition-all duration-[250ms] hover:-translate-y-0.5">
                  Send Message
                </Button>
              </form>
            </Form>
          </div>

          <div className="space-y-8 animate-fade-in-up [animation-delay:150ms]">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 shadow-medium">
              <h3 className="text-white font-display text-2xl font-semibold mb-6">
                Get In Touch
              </h3>
              <div className="space-y-6">
                {contactInfo.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[oklch(0.50_0.13_150)]/20 flex items-center justify-center group-hover:bg-[oklch(0.50_0.13_150)]/30 transition-colors duration-[250ms]">
                      <item.icon className="w-6 h-6 text-[oklch(0.50_0.13_150)]" />
                    </div>
                    <div>
                      <p className="text-[oklch(0.90_0_0)] text-sm font-medium mb-1">
                        {item.label}
                      </p>
                      {item.href ? <Link to={item.href} className="text-white hover:text-[oklch(0.50_0.13_150)] transition-colors duration-[250ms]">
                          {item.value}
                        </Link> : <p className="text-white">{item.value}</p>}
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden shadow-medium">
              <AspectRatio ratio={16 / 9}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1399276/img/contact_location_map.png" alt="Aran Group Office Location" className="w-full h-full object-cover" />
              </AspectRatio>
            </div>
          </div>
        </div>
      </div>
    </section>;
}